import { Box, Center, Text, VStack } from "@chakra-ui/react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import { Discipline_GET } from "../../../Api/dto/Discipline_GET";
import { SignupRequest } from "../../../Api/dto/SignupRequest_POST";
import { Gender } from "../../../Api/dto/Discipline_POST_PUT";
import { toast } from "react-toastify";
import { SignupUnit } from "./SignupFlow";

export function DragAndDropExcelWithSignups(props: {
  selectedDiscipline: Discipline_GET | null;
  onParsedSignups: (data: Array<SignupUnit>) => void;
  onParseFailure: () => void;
}) {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const sizeOfGroup = props.selectedDiscipline?.groupSize || 1;
      const isGroupSignup = sizeOfGroup > 1;
      const fileContentPromises = acceptedFiles.flatMap(async (file) => {
        return await parseFile(file).then((content) => {
          return content.map((row) => {
            return {
              disciplineId: props.selectedDiscipline?.id,
              firstname: row.firstName,
              lastname: row.lastName,
              groupName: isGroupSignup ? row.teamName : null,
              sportsTeam: isGroupSignup ? null : row.teamName,
              fullBirthYear: parseBirthYearFromRow(row.birthDateOrYearOrSsn),
              fullBirthDate: parseBirthDateFromRow(row.birthDateOrYearOrSsn),
              socialSecurityNumber: parseSsnLastFourDigitsFromRow(row.birthDateOrYearOrSsn),
              gender:
                row.gender.toLowerCase() == "man" ? Gender.MAN : Gender.WOMAN,
            } as SignupRequest;
          });
        });
      });

      try {
        const allEntries = (await Promise.all(fileContentPromises))
          .flatMap((content) => content)
          .map((e) => ({ ...e, disciplineId: props.selectedDiscipline?.id }));

        const allGroupNames = new Set(
          isGroupSignup ? allEntries.map((e) => e.groupName) : []
        );

        let newSignupUnits = [] as Array<SignupUnit>;

        for (let groupName of allGroupNames) {
          if (!groupName?.trim()) {
            continue;
          }
          const signupCountInGroup = allEntries.filter(
            (e) => e.groupName === groupName
          ).length;
          if (signupCountInGroup > sizeOfGroup) {
            toast.error(
              `Lag ${groupName} ska vara av storlek ${sizeOfGroup}, du har angivit ${signupCountInGroup}!`
            );
            throw new Error("Group is now overful!");
          } else if (signupCountInGroup < sizeOfGroup) {
            for (let i = 0; i < sizeOfGroup - signupCountInGroup; i++) {
              allEntries.push({
                disciplineId: props.selectedDiscipline?.id,
                firstname: "VAKANT",
                lastname: "VAKANT",
                groupName: groupName,
                sportsTeam: undefined,
                fullBirthYear: undefined,
                gender: undefined,
                socialSecurityNumber: undefined,
              });
            }
          }
          newSignupUnits = [
            ...newSignupUnits,
            allEntries.filter((e) => e.groupName === groupName),
          ];
        }

        if (newSignupUnits.length === 0) {
          newSignupUnits = allEntries.map((e) => [e]);
        }
        console.log("Parsed2");

        props.onParsedSignups(newSignupUnits);
      } catch (e) {
        props.onParseFailure();
      }
    },
    [props.selectedDiscipline]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
  });

  if (props.selectedDiscipline == null) {
    return null;
  }

  return (
    <Box
      {...getRootProps()}
      padding={"20px"}
      shadow={"2xl"}
      borderWidth={"3px"}
      borderColor={"black"}
      background={isDragActive ? "green.300" : "blue.200"}
      minHeight={"200px"}
      margin={"40px"}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <Center>
          <Text fontSize={"20px"}>Släpp filerna här...</Text>
        </Center>
      ) : (
        <VStack>
          <Text
            fontSize={"40px"}
          >{`Excel anmälan för ${props.selectedDiscipline.name}`}</Text>
          <Text>Välj fil(er) genom att klicka eller dra filen hit!</Text>
        </VStack>
      )}
    </Box>
  );
}

export interface RowData {
  firstName: string;
  lastName: string;
  birthDateOrYearOrSsn: string | number;
  teamName: string;
  gender: string;
}

// Define headers in the correct order as they appear in the file (but use your own keys)
const customHeaders: (keyof RowData)[] = [
  "firstName",
  "lastName",
  "birthDateOrYearOrSsn",
  "teamName",
  "gender",
];

export async function parseFile(file: File): Promise<RowData[]> {
  const isCSV = file.name.endsWith(".csv");

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result;
        if (!content) {
          reject("File is empty");
          return;
        }

        let workbook: XLSX.WorkBook;

        if (isCSV) {
          const text = new TextDecoder("utf-8").decode(content as ArrayBuffer);
          workbook = XLSX.read(text, { type: "string" });
        } else {
          workbook = XLSX.read(content, { type: "array" });
        }

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1, // Get raw row arrays
          defval: "",
        });

        rows.shift(); // Remove original header row

        const mapped: RowData[] = rows.map((row) => {
          const obj = {} as RowData;
          customHeaders.forEach((key, i) => {
            obj[key] = row[i] ?? "";
          });
          return obj;
        });

        resolve(mapped);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsArrayBuffer(file);
  });
}


function parseBirthYearFromRow(birthDateOrYearOrSsn: string | number): number | null {
  const input = `${birthDateOrYearOrSsn}`.replaceAll(/\D/g,"");
  return parseInt(input.slice(0,4));
}

function parseBirthDateFromRow(birthDateOrYearOrSsn: string | number): Date | null {
  const input = `${birthDateOrYearOrSsn}`.replaceAll(/-\d+/g,"").replaceAll(/\D/g,"");

  let year = null;
  let month = 0;
  let day = 1;

  if (input.length === 4) {
    year = parseInt(input, 10);
  }
  else if (input.length === 8) {
    year = parseInt(input.slice(0, 4), 10);
    month = parseInt(input.slice(4, 6), 10);
    day = parseInt(input.slice(6, 8), 10);
  }
  else {
    return null;
  }

  return new Date(year, month - 1, day);
}

function parseSsnLastFourDigitsFromRow(
  input: string | number
): string {
  const match = String(input).match(/-(\d{4})$/);
  return match?.[1] ?? "XXXX";
}